import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Bell, Share2, Eye, Save, Undo2, Redo2, Minus, Plus,
  Maximize, RotateCcw, Upload, Trash2, Download, Printer, Link2, X, Check,
} from "lucide-react";
import { CrestSVG, ShapeThumb, SymbolThumb, BannerThumb } from "./CrestSVG";
import {
  SHAPES, SYMBOLS, BANNERS, FOLIAGE, CREST_PLACEMENTS, COLOR_FIELDS, PALETTES, PRESETS, BANNER_ALIGNS, CROWNS,
} from "./crestConfig";
import { CREST_FONTS, serializeSvgWithFonts } from "./crestFonts";
import { CREST_CROWN_IMAGES } from "./crestCrownImages";
import "./crest-studio.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = () =>
  axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
  });

const LAYOUT_TOGGLES = [
  { key: "showCrown", label: "Show Crown" },
  { key: "showFoliage", label: "Show Foliage" },
  { key: "showBanner", label: "Show Banner" },
  { key: "showMotto", label: "Show Motto" },
  { key: "showEstablishedYear", label: "Show Established Year" },
  { key: "showFamilyName", label: "Show Family Name on Banner" },
  { key: "doubleShieldBorder", label: "Use Double Shield Border" },
];

export default function FamilyCrestStudio() {
  const navigate = useNavigate();
  const [crest, setCrest] = useState(null);
  const [savedCrest, setSavedCrest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("elements");
  const [zoom, setZoom] = useState(100);
  const [showShare, setShowShare] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);
  const history = useRef({ past: [], future: [] });

  useEffect(() => {
    api().get("/family-tree").then((r) => {
      setCrest(r.data.crest);
      setSavedCrest(r.data.crest);
    }).catch(() => toast.error("Could not load your crest.")).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const commit = useCallback((next) => {
    setCrest((prev) => {
      history.current.past.push(prev);
      history.current.future = [];
      return next;
    });
    setDirty(true);
  }, []);

  const update = (patch) => commit({ ...crest, ...patch });

  const undo = () => {
    const h = history.current;
    if (!h.past.length) return;
    h.future.push(crest);
    setCrest(h.past.pop());
    setDirty(true);
  };
  const redo = () => {
    const h = history.current;
    if (!h.future.length) return;
    h.past.push(crest);
    setCrest(h.future.pop());
    setDirty(true);
  };

  function crestToCanvas() {
    return new Promise((resolve, reject) => {
      const svg = document.querySelector("#crest-live svg");
      if (!svg) return reject(new Error("no svg"));
      serializeSvgWithFonts(svg).then((data) => {
        const img = new Image();
        const url = URL.createObjectURL(new Blob([data], { type: "image/svg+xml;charset=utf-8" }));
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 780; canvas.height = 1020;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          resolve(canvas);
        };
        img.onerror = reject;
        img.src = url;
      }).catch(reject);
    });
  }

  async function uploadRender() {
    try {
      const canvas = await crestToCanvas();
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png", 0.92));
      if (!blob) return;
      const form = new FormData();
      form.append("file", new File([blob], "crest.png", { type: "image/png" }));
      await axios.post(`${BACKEND_URL}/api/family-tree/crest/render`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
      });
    } catch (e) { /* non-blocking: PDF stamp is best-effort */ }
  }

  async function save(returnToTree = false) {
    if (!crest.familyName?.trim()) { toast.error("Family name is required."); return; }
    if (crest.establishedYear && !/^\d{3,4}$/.test(String(crest.establishedYear).trim())) {
      toast.error("Established year should be a 3–4 digit year."); return;
    }
    try {
      const r = await api().put("/family-tree", { crest });
      setSavedCrest(r.data.crest);
      setCrest(r.data.crest);
      setDirty(false);
      await uploadRender();
      toast.success("Family crest saved");
      if (returnToTree) navigate("/family-tree");
    } catch (e) {
      toast.error("Could not save the crest.");
    }
  }

  function applyPreset(preset) {
    if (dirty && !window.confirm("Load this design? Your unsaved changes will be replaced.")) return;
    commit({ ...crest, ...preset.crest, customSymbolUrl: crest.customSymbolUrl });
    toast.success(`${preset.name} loaded`);
  }

  async function onUpload(file) {
    if (!file) return;
    const okTypes = ["image/svg+xml", "image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) { toast.error("Use SVG, PNG, JPG or WEBP."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)."); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${BACKEND_URL}/api/family-tree/crest/symbol`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
      });
      setCrest((c) => ({ ...c, symbol: "custom", customSymbolUrl: r.data.customSymbolUrl }));
      setSavedCrest((c) => ({ ...c, symbol: "custom", customSymbolUrl: r.data.customSymbolUrl }));
      setDirty(true);
      toast.success("Custom symbol added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload the symbol.");
    } finally {
      setUploading(false);
    }
  }

  async function removeSymbol() {
    try {
      const r = await api().delete("/family-tree/crest/symbol");
      setCrest((c) => ({ ...c, symbol: r.data.symbol, customSymbolUrl: null }));
      setSavedCrest((c) => ({ ...c, symbol: r.data.symbol, customSymbolUrl: null }));
      toast.success("Custom symbol removed");
    } catch (e) {
      toast.error("Could not remove the symbol.");
    }
  }

  function downloadSVG() {
    const svg = document.querySelector("#crest-live svg");
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml" });
    triggerDownload(URL.createObjectURL(blob), `${(crest.familyName || "family").replace(/\s+/g, "-")}-crest.svg`);
  }

  function downloadPNG() {
    crestToCanvas().then((canvas) => {
      triggerDownload(canvas.toDataURL("image/png"), `${(crest.familyName || "family").replace(/\s+/g, "-")}-crest.png`);
    }).catch(() => toast.error("Could not export PNG."));
  }

  function triggerDownload(href, name) {
    const a = document.createElement("a");
    a.href = href; a.download = name; a.click();
  }

  if (loading || !crest) {
    return <div className="crest-studio"><div className="crest-loading">Loading your crest…</div></div>;
  }

  return (
    <div className="crest-studio" data-testid="crest-studio">
      {/* Header */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-icon-btn" onClick={() => navigate("/family-tree")} aria-label="Back to family tree" data-testid="crest-back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="cs-title">Family Crest</h1>
            <p className="cs-subtitle">Create a unique crest to represent your family heritage.</p>
          </div>
        </div>
        <div className="cs-header-right">
          <button className="cs-icon-btn" aria-label="Search"><Search size={18} /></button>
          <button className="cs-icon-btn" aria-label="Notifications"><Bell size={18} /></button>
          <button className="cs-share-btn" onClick={() => setShowShare(true)} data-testid="crest-share-btn">
            <Share2 size={16} /> Share Crest
          </button>
        </div>
      </header>

      {/* Toolbar */}
      <div className="cs-toolbar">
        <div className="cs-tabs" role="tablist">
          {["elements", "colors", "layout"].map((t) => (
            <button key={t} role="tab" aria-selected={tab === t}
              className={`cs-tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}
              data-testid={`crest-tab-${t}`}>
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="cs-toolbar-actions">
          <button className="cs-btn-ghost" onClick={() => { setZoom(100); toast("Preview reset"); }}><Eye size={16} /> Preview</button>
          <button className="cs-btn-dark" onClick={() => save(false)} data-testid="crest-save-top"><Save size={16} /> Save Crest</button>
        </div>
      </div>

      {/* Workspace */}
      <div className="cs-workspace">
        {/* LEFT PANEL */}
        <aside className="cs-panel cs-left" data-testid="crest-left-panel">
          {tab === "elements" && (
            <>
              <PanelSection title="Shield Shape">
                <div className="cs-grid-5">
                  {SHAPES.map((s) => (
                    <OptionBtn key={s.id} active={crest.shape === s.id} label={s.label}
                      testid={`shape-${s.id}`} onClick={() => update({ shape: s.id })}>
                      <ShapeThumb shape={s.id} color={crest.shape === s.id ? "#071B33" : "#64748b"} />
                    </OptionBtn>
                  ))}
                </div>
              </PanelSection>

              <PanelSection title="Symbol">
                <div className="cs-grid-4">
                  {SYMBOLS.map((s) => (
                    <OptionBtn key={s.id} active={crest.symbol === s.id} label={s.label}
                      testid={`symbol-${s.id}`} onClick={() => update({ symbol: s.id })}>
                      <SymbolThumb symbol={s.id} color={crest.symbol === s.id ? "#071B33" : "#64748b"} />
                    </OptionBtn>
                  ))}
                </div>
              </PanelSection>

              <PanelSection title="Crown Topper">
                <div className="cs-grid-4">
                  {CROWNS.map((c) => (
                    <OptionBtn key={c.id}
                      active={crest.showCrown && (crest.crownStyle || "royal_classic") === c.id}
                      label={c.label} testid={`crown-${c.id}`}
                      onClick={() => update({ crownStyle: c.id, showCrown: true })}>
                      <img src={CREST_CROWN_IMAGES[c.id]} alt="" className="cs-crown-thumb" />
                    </OptionBtn>
                  ))}
                </div>
              </PanelSection>

              <PanelSection title="Banner Style">
                <div className="cs-grid-4">
                  {BANNERS.map((b) => (
                    <OptionBtn key={b.id} active={crest.bannerStyle === b.id} label={b.label}
                      testid={`banner-${b.id}`} onClick={() => update({ bannerStyle: b.id })}>
                      <BannerThumb style={b.id} color={crest.bannerStyle === b.id ? "#071B33" : "#94a3b8"} />
                    </OptionBtn>
                  ))}
                </div>
              </PanelSection>

              <PanelSection title="Font (Family Name, Motto & Year)">
                <div className="cs-chip-row">
                  {CREST_FONTS.map((f) => (
                    <button key={f.id} type="button"
                      className={`cs-chip ${(crest.fontFamily || "serif") === f.id ? "active" : ""}`}
                      style={{ fontFamily: f.stack, fontSize: f.id === "script" ? 18 : 14 }}
                      onClick={() => update({ fontFamily: f.id })} data-testid={`font-${f.id}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </PanelSection>
            </>
          )}

          {tab === "colors" && (
            <>
              <PanelSection title="Preset Palettes">
                <div className="cs-palettes">
                  {PALETTES.map((p) => (
                    <button key={p.id} className="cs-palette" onClick={() => update({ ...p.apply })}
                      data-testid={`palette-${p.id}`}>
                      <span className="cs-palette-swatches">
                        {p.swatches.map((c, i) => <span key={i} style={{ background: c }} />)}
                      </span>
                      <span>{p.name}</span>
                    </button>
                  ))}
                </div>
              </PanelSection>

              <PanelSection title="Colors">
                {COLOR_FIELDS.map((f) => (
                  <div className="cs-color-row" key={f.key}>
                    <label>{f.label}</label>
                    <div className="cs-color-controls">
                      <input type="color" value={crest[f.key] || f.def} onChange={(e) => update({ [f.key]: e.target.value })}
                        aria-label={`${f.label} color`} data-testid={`color-${f.key}`} />
                      <input type="text" value={crest[f.key] || f.def} onChange={(e) => update({ [f.key]: e.target.value })}
                        className="cs-hex" aria-label={`${f.label} hex`} />
                      <button className="cs-icon-mini" title="Reset" onClick={() => update({ [f.key]: f.def })}
                        aria-label={`Reset ${f.label}`}><RotateCcw size={14} /></button>
                    </div>
                  </div>
                ))}
              </PanelSection>
            </>
          )}

          {tab === "layout" && (
            <>
              <PanelSection title="Toggles">
                {LAYOUT_TOGGLES.map((t) => (
                  <label className="cs-toggle" key={t.key} data-testid={`toggle-${t.key}`}>
                    <span>{t.label}</span>
                    <input type="checkbox" checked={!!crest[t.key]} onChange={(e) => update({ [t.key]: e.target.checked })} />
                    <span className="cs-switch" aria-hidden="true" />
                  </label>
                ))}
              </PanelSection>

              <PanelSection title="Foliage Style">
                <div className="cs-chip-row">
                  {FOLIAGE.map((f) => (
                    <button key={f.id} className={`cs-chip ${crest.foliageStyle === f.id ? "active" : ""}`}
                      onClick={() => update({ foliageStyle: f.id })} data-testid={`foliage-${f.id}`}>{f.label}</button>
                  ))}
                </div>
              </PanelSection>

              <PanelSection title="Banner Text Alignment">
                <div className="cs-chip-row">
                  {BANNER_ALIGNS.map((a) => (
                    <button key={a.id} className={`cs-chip ${(crest.bannerTextAlign || "center") === a.id ? "active" : ""}`}
                      onClick={() => update({ bannerTextAlign: a.id })} data-testid={`banner-align-${a.id}`}>{a.label}</button>
                  ))}
                </div>
                <div className="cs-color-row" style={{ marginTop: 10 }}>
                  <label>Banner text position</label>
                  <div className="cs-color-controls" style={{ gap: 10 }}>
                    <input type="range" min={-10} max={10} step={1}
                      value={Number(crest.bannerTextOffsetY) || 0}
                      onChange={(e) => update({ bannerTextOffsetY: Number(e.target.value) })}
                      aria-label="Banner text vertical position" data-testid="banner-offset-y"
                      style={{ flex: 1 }} />
                    <button className="cs-icon-mini" title="Reset position"
                      onClick={() => update({ bannerTextOffsetY: 0 })} aria-label="Reset banner text position">
                      <RotateCcw size={14} />
                    </button>
                  </div>
                </div>
              </PanelSection>

              <PanelSection title="Crest Placement on Family Tree">
                <div className="cs-chip-row">
                  {CREST_PLACEMENTS.map((p) => (
                    <button key={p.id} className={`cs-chip ${crest.crestPlacement === p.id ? "active" : ""}`}
                      onClick={() => update({ crestPlacement: p.id })} data-testid={`placement-${p.id}`}>{p.label}</button>
                  ))}
                </div>
              </PanelSection>
            </>
          )}
        </aside>

        {/* CENTER PREVIEW */}
        <main className="cs-preview" data-testid="crest-preview">
          <div className="cs-preview-stage">
            <div id="crest-live" style={{ transform: `scale(${zoom / 100})`, transition: "transform .15s ease" }}>
              <CrestSVG crest={crest} size={320} />
            </div>
          </div>
          <div className="cs-preview-controls">
            <button className="cs-icon-mini" onClick={undo} aria-label="Undo" data-testid="crest-undo"><Undo2 size={16} /></button>
            <button className="cs-icon-mini" onClick={redo} aria-label="Redo" data-testid="crest-redo"><Redo2 size={16} /></button>
            <span className="cs-sep" />
            <button className="cs-icon-mini" onClick={() => setZoom((z) => Math.max(60, z - 10))} aria-label="Zoom out"><Minus size={16} /></button>
            <input type="range" min={60} max={150} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} aria-label="Zoom" data-testid="crest-zoom" />
            <button className="cs-icon-mini" onClick={() => setZoom((z) => Math.min(150, z + 10))} aria-label="Zoom in"><Plus size={16} /></button>
            <span className="cs-zoom-pct" data-testid="crest-zoom-pct">{zoom}%</span>
            <button className="cs-icon-mini" onClick={() => setZoom(100)} aria-label="Fit to screen"><Maximize size={16} /></button>
          </div>
        </main>

        {/* RIGHT DETAILS */}
        <aside className="cs-panel cs-right" data-testid="crest-right-panel">
          <h3 className="cs-panel-heading">Crest Details</h3>
          <Field label="Family Name" value={crest.familyName} onChange={(v) => update({ familyName: v })} testid="crest-family-name" required />
          <Field label="Motto" value={crest.motto} onChange={(v) => update({ motto: v })} testid="crest-motto" />
          <Field label="Established Year" value={crest.establishedYear} onChange={(v) => update({ establishedYear: v })} testid="crest-year" />
          <Field label="Optional Subtitle" value={crest.subtitle} onChange={(v) => update({ subtitle: v })} />
          <Field label="Optional Family Origin" value={crest.familyOrigin} onChange={(v) => update({ familyOrigin: v })} />
          <Field label="Optional Founder Name" value={crest.founderName} onChange={(v) => update({ founderName: v })} />

          <div className="cs-upload-label">Upload Custom Symbol</div>
          <div
            className="cs-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0]); }}
            data-testid="crest-dropzone"
          >
            {crest.customSymbolUrl ? (
              <div className="cs-custom-preview">
                <img src={crest.customSymbolUrl.startsWith("/api") ? `${BACKEND_URL}${crest.customSymbolUrl}` : crest.customSymbolUrl} alt="Custom symbol" />
                <div className="cs-custom-actions">
                  <button className="cs-btn-ghost" onClick={() => fileRef.current?.click()}><Upload size={14} /> Replace</button>
                  <button className="cs-btn-ghost danger" onClick={removeSymbol} data-testid="crest-symbol-remove"><Trash2 size={14} /> Remove</button>
                </div>
              </div>
            ) : (
              <>
                <Upload size={22} />
                <p>Drag and drop your image<br /><span>or</span></p>
                <button className="cs-btn-outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="crest-choose-file">
                  {uploading ? "Uploading…" : "Choose File"}
                </button>
                <small>SVG, PNG or JPG (max. 2MB)</small>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/svg+xml,image/png,image/jpeg,image/webp"
              style={{ display: "none" }} onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="crest-file-input" />
          </div>

          <div className="cs-save-row">
            <button className="cs-btn-dark full" onClick={() => save(false)} data-testid="crest-save-panel"><Save size={16} /> Save Crest</button>
            <button className="cs-btn-outline full" onClick={() => save(true)} data-testid="crest-save-return">Save & Return to Tree</button>
            <button className="cs-btn-ghost full" onClick={() => { setCrest(savedCrest); setDirty(false); toast("Reset to saved version"); }}>Reset to Saved Version</button>
            <button className="cs-btn-ghost full" onClick={() => navigate("/family-tree")}>Cancel</button>
          </div>
        </aside>
      </div>

      {/* Preset gallery */}
      <section className="cs-gallery" data-testid="crest-gallery">
        <h2>Crest Design Examples</h2>
        <p>Choose a starting point and customize it to represent your family.</p>
        <div className="cs-gallery-grid">
          {PRESETS.map((p) => (
            <button key={p.id} className="cs-preset-card" onClick={() => applyPreset(p)} data-testid={`preset-${p.id}`}>
              <div className="cs-preset-crest"><CrestSVG crest={{ ...p.crest }} size={150} /></div>
              <strong>{p.name}</strong>
              <small>{p.tagline}</small>
              <span className="cs-preset-swatches">
                {[p.crest.primaryColor, p.crest.accentColor, p.crest.foliageColor, p.crest.secondaryColor].map((c, i) => (
                  <span key={i} style={{ background: c }} />
                ))}
              </span>
            </button>
          ))}
        </div>
      </section>

      {showShare && (
        <ShareModal crest={crest} onClose={() => setShowShare(false)} onPNG={downloadPNG} onSVG={downloadSVG}
          onPrint={() => window.print()} />
      )}
    </div>
  );
}

function PanelSection({ title, children }) {
  return (
    <section className="cs-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function OptionBtn({ active, label, children, onClick, testid }) {
  return (
    <button type="button" className={`cs-option ${active ? "active" : ""}`} onClick={onClick}
      aria-pressed={active} title={label} data-testid={testid}>
      {children}
      <span className="cs-option-label">{label}</span>
    </button>
  );
}

function Field({ label, value, onChange, testid, required }) {
  return (
    <label className="cs-field">
      <span>{label}{required && <em> *</em>}</span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}

function ShareModal({ crest, onClose, onPNG, onSVG, onPrint }) {
  const [copying, setCopying] = useState(false);
  const [link, setLink] = useState("");

  function fallbackCopy(text) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success("Secure share link copied");
      else toast("Link ready below — press Ctrl+C to copy");
    } catch {
      toast("Link ready below — press Ctrl+C to copy");
    }
  }

  async function copyLink() {
    setCopying(true);
    let url;
    try {
      const r = await api().post("/family-tree/share");
      url = `${window.location.origin}/family-tree/shared/${r.data.shareToken}`;
      setLink(url);
    } catch (e) {
      toast.error("Could not create the share link.");
      setCopying(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Secure share link copied");
    } catch {
      fallbackCopy(url);
    } finally {
      setCopying(false);
    }
  }

  return (
    <div className="cs-modal-backdrop" onClick={onClose} data-testid="crest-share-modal">
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cs-modal-head">
          <h3>Share “{crest.familyName || "Family"}” Crest</h3>
          <button className="cs-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="cs-share-grid">
          <button onClick={copyLink} disabled={copying} data-testid="share-copy-link"><Link2 size={18} /> {copying ? "Creating…" : "Copy secure link"}</button>
          <button onClick={onPNG} data-testid="share-png"><Download size={18} /> Download PNG</button>
          <button onClick={onSVG} data-testid="share-svg"><Download size={18} /> Download SVG</button>
          <button onClick={onPrint} data-testid="share-print"><Printer size={18} /> Print</button>
        </div>
        {link && (
          <div className="cs-share-link-row" style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input readOnly value={link} onFocus={(e) => e.target.select()} data-testid="share-link-input"
              style={{ flex: 1, border: "1px solid var(--cs-border)", borderRadius: 9, padding: 10, fontSize: 13 }} />
            <button className="cs-btn-outline" onClick={() => fallbackCopy(link)}>Copy</button>
          </div>
        )}
        <p className="cs-share-note"><Check size={14} /> Shared links are private and can be revoked from the Family Tree share settings.</p>
      </div>
    </div>
  );
}
